import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const VIEWER_ONLY_STORAGE_KEY = 'doctorViewerOnly';
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

export const VIEWER_ONLY_ERROR_CODE = 'ERR_VIEWER_ONLY';
export const VIEWER_ONLY_MESSAGE = 'This account is viewer only. Adding, editing, and deleting data is disabled.';

export class ViewerOnlyError extends Error {
  code = VIEWER_ONLY_ERROR_CODE;

  constructor() {
    super(VIEWER_ONLY_MESSAGE);
    this.name = 'ViewerOnlyError';
  }
}

export const setViewerOnlySession = (enabled: boolean): void => {
  if (enabled) {
    localStorage.setItem(VIEWER_ONLY_STORAGE_KEY, 'true');
  } else {
    localStorage.removeItem(VIEWER_ONLY_STORAGE_KEY);
  }
  window.dispatchEvent(new Event('viewerOnlyChanged'));
};

export const isViewerOnlySession = (): boolean =>
  localStorage.getItem(VIEWER_ONLY_STORAGE_KEY) === 'true';

export const assertViewerCanWrite = (): void => {
  if (isViewerOnlySession()) {
    throw new ViewerOnlyError();
  }
};

export const isViewerOnlyError = (error: unknown): boolean => {
  if (error instanceof ViewerOnlyError) return true;
  if (axios.isAxiosError(error)) {
    return error.code === VIEWER_ONLY_ERROR_CODE ||
      (error.response?.status === 403 && error.response?.data?.code === 'viewer-only');
  }
  return false;
};

let interceptorId: number | null = null;

/**
 * Installs one dashboard-wide request interceptor.
 *
 * Unsafe HTTP methods are rejected locally for viewer-only dashboard sessions.
 */
export const configureViewerOnlyHttpProtection = (): void => {
  if (interceptorId !== null) return;

  interceptorId = axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const method = (config.method || 'get').toLowerCase();
    if (isViewerOnlySession() && WRITE_METHODS.has(method)) {
      const error = new AxiosError(VIEWER_ONLY_MESSAGE, VIEWER_ONLY_ERROR_CODE, config);
      return Promise.reject(error);
    }

    return config;
  });
};
